import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, DropDownList, Label } from '@trucktrax/trucktrax-common';
import {
  LocationDto,
  PlantDto,
  ProductLine,
  TicketDto,
  TicketGeozoneDto,
} from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';

import AddFeatureModal from '../../AddFeatureModal';
import CardMap from '../../forms/CardMap';
import DeliveryAddressPicker from './DeliveryAddressPicker';
import DivertPlantPicker from './DivertPlantPicker';
import { updateDeliveryLocation } from '../../../../services/ticketsService';
import {
  plantToTicketGeozone,
  circleToTicketGeozone,
  polygonPointsToTicketGeozone,
  DEFAULT_DELIVERY_GEOZONE_RADIUS_METERS,
  DivertPlantOption,
} from '../../../../util/deliveryLocationUtil';
import { ResolvedAddress } from '../../../../util/placesUtil';
import { isLocationValid } from '../../../../util/geoUtil';
import { openModal, closeModal } from '../../../../store/actions/errorModalActions';
import { openSnackbar } from '../../../../store/actions/snackbarActions';
import {
  needToRefreshData,
  selectTicket,
  syncTicket,
} from '../../../../store/actions/ticketActions';
import styles from './UpdateDeliveryLocationModal.module.css';
import {
  ACCENT,
  APPLY_LABEL,
  ARE_YOU_SURE,
  CANCEL_LABEL,
  ERROR,
  SUCCESS,
} from '../../../../constants/appConstants';
import {
  DELIVERY_LOCATION_CONFIRM,
  DELIVERY_LOCATION_ENTER_ADDRESS,
  DELIVERY_LOCATION_LOOKUP_ERROR,
  DELIVERY_LOCATION_SELECT_PLANT,
  DELIVERY_LOCATION_UPDATE_ERROR,
  DELIVERY_LOCATION_UPDATE_SUCCESS,
  DIVERSION_REASON_LABEL,
  DIVERSION_REASON_NOTES_MAX_LENGTH,
  DIVERSION_REASON_NOTES_PLACEHOLDER,
  DIVERSION_REASON_OPTIONS,
  DIVERSION_REASON_OTHER,
  UPDATE_DELIVERY_LOCATION_TITLE,
} from '../../../../constants/deliveryLocationConstants';

type DeliveryMode = 'plant' | 'address';

interface MapChangeEvent {
  location?: LocationDto;
  circle?: { center: LocationDto; radius: number };
  polygon?: { points: LocationDto[] };
}

// Rebuild the visible delivery-address string from a plant's address parts.
// Output matches the existing format TicketDetails splits on (first \n separates
// line 1 from city/state/zip): e.g. "4301 Pioneer Ave\nDuPont, WA 98327".
const formatPlantAddress = (plant: PlantDto): string => {
  const line1 = [plant.addressLineOne, plant.addressLineTwo]
    .map(s => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const cityState = [plant.city, plant.state]
    .map(s => (s ?? '').trim())
    .filter(Boolean)
    .join(', ');
  const line2 = [cityState, (plant.zipCode ?? '').trim()].filter(Boolean).join(' ');
  return `${line1}\n${line2}`;
};

export interface UpdateDeliveryLocationModalOwnProps {
  selectedTicket: TicketDto;
  ticketProductLine?: ProductLine | null;
  isOpen: boolean;
  onClose: () => void;
}

interface DispatchProps {
  openModal: typeof openModal;
  closeModal: typeof closeModal;
  openSnackbar: typeof openSnackbar;
  needToRefreshData: typeof needToRefreshData;
  selectTicket: typeof selectTicket;
  syncTicket: typeof syncTicket;
}

type UpdateDeliveryLocationModalProps = UpdateDeliveryLocationModalOwnProps & DispatchProps;

interface UpdateDeliveryLocationModalState {
  mode: DeliveryMode;
  geoZone: TicketGeozoneDto | null;
  mapValue: {
    circle?: { center: LocationDto; radius: number };
    location?: LocationDto;
    polygon?: { points: LocationDto[] };
  };
  // The full PlantDto selected in plant mode. Kept so applyChange can rebuild the
  // ticket's visible `address` field from the plant's address parts — otherwise
  // TicketDetails keeps rendering the pre-divert address even though geoZone moved.
  selectedPlant: PlantDto | null;
  // In address mode, the canonical formatted address the dispatcher confirmed via
  // the picker. Forwarded to the ticket on apply so the panel stops showing the
  // pre-divert address. Empty until a match is confirmed.
  confirmedAddress: string;
  // The optional diversion reason, as the backend enum member name; empty when none is
  // chosen. Survives a plant/address mode switch — the reason describes the divert, not
  // the destination.
  diversionReason: string;
  // Freeform notes for the "Other" reason. Only sent when Other is selected.
  diversionReasonNotes: string;
  submitting: boolean;
}

export class UpdateDeliveryLocationModal extends Component<
  UpdateDeliveryLocationModalProps,
  UpdateDeliveryLocationModalState
> {
  state: UpdateDeliveryLocationModalState = {
    mode: 'plant',
    geoZone: null,
    mapValue: {},
    selectedPlant: null,
    confirmedAddress: '',
    diversionReason: '',
    diversionReasonNotes: '',
    submitting: false,
  };

  // Wrapper around the plant Autocomplete so we can focus its input after the
  // user clicks "Select a plant" (Autocomplete doesn't expose a ref/autoFocus).
  private plantModeRef = React.createRef<HTMLDivElement>();

  private setMode = (mode: DeliveryMode): void => {
    // Switching modes clears any pending selection so we never send a stale geozone.
    // (Switching to plant unmounts DeliveryAddressPicker, resetting its own state.)
    this.setState(
      {
        mode,
        geoZone: null,
        mapValue: {},
        selectedPlant: null,
        confirmedAddress: '',
      },
      () => {
        if (mode === 'plant') {
          // Focus the plant search input so the user can start typing immediately.
          // Autocomplete sets data-test="search-input" directly on the <input>
          // element, and its onFocus handler opens the suggestions dropdown.
          const input = this.plantModeRef.current?.querySelector<HTMLInputElement>(
            'input[data-test="search-input"]',
          );
          input?.focus();
        }
      },
    );
  };

  private handlePlantSelected = (plant: DivertPlantOption): void => {
    // Keep the divert selection local — never dispatch setCurrentPlant / mutate map filters.
    const geoZone = plantToTicketGeozone(plant);
    // Pull the circle out of the ticket geozone (plantToTicketGeozone always yields a
    // circle when it yields anything — v2 ticket zones are circle-only).
    const zoneCircle = geoZone?.zone?.circle;
    const circle =
      zoneCircle && zoneCircle.center && zoneCircle.radius
        ? { center: zoneCircle.center, radius: zoneCircle.radius }
        : undefined;
    // Prefer the plant's own coordinates for the map marker, then fall back to the
    // geozone center so the map still recenters when the plant has no top-level location.
    const location =
      plant.location && isLocationValid(plant.location) ? plant.location : circle?.center;
    this.setState({
      geoZone,
      mapValue: { location, circle },
      selectedPlant: plant,
    });
  };

  // The picker resolved a picked prediction: seed a default 0.10-mile geozone centered
  // on it (enables Apply) and show the map. The dispatcher can still resize the circle
  // on the map; handleMapChange then replaces both mapValue.circle and geoZone.
  private handleAddressConfirmed = (resolved: ResolvedAddress): void => {
    const { location } = resolved;
    const circle = { center: location, radius: DEFAULT_DELIVERY_GEOZONE_RADIUS_METERS };
    this.setState({
      geoZone: circleToTicketGeozone(circle),
      mapValue: { location, circle },
      confirmedAddress: resolved.formattedAddress,
    });
  };

  // The picker's field was edited/cleared: drop the geozone (re-disables Apply) and the
  // map so the dispatcher must re-confirm a match before moving forward.
  private handleAddressCleared = (): void => {
    this.setState({ geoZone: null, mapValue: {}, confirmedAddress: '' });
  };

  private handleLookupError = (message: string): void => {
    this.props.openSnackbar({
      snackbarBody: message || DELIVERY_LOCATION_LOOKUP_ERROR,
      dataTest: 'delivery-location-lookup-error-snackbar',
      snackbarType: ERROR as any,
    });
  };

  // Mirrors handleLookupError for the plant mode: DivertPlantPicker reports a failed
  // tenant-wide fetch and the modal owns the snackbar, so the picker holds no store access.
  private handlePlantListError = (message: string): void => {
    this.props.openSnackbar({
      snackbarBody: message,
      dataTest: 'delivery-location-plant-list-error-snackbar',
      snackbarType: ERROR as any,
    });
  };

  // A non-Other selection (including None) drops any notes: the card sends notes only
  // with Other, and keeping hidden text around risks submitting it invisibly later.
  private handleReasonSelected = (selected: { value?: string }): void => {
    const diversionReason = selected.value ?? '';
    this.setState(prev => ({
      diversionReason,
      diversionReasonNotes:
        diversionReason === DIVERSION_REASON_OTHER ? prev.diversionReasonNotes : '',
    }));
  };

  private handleNotesChanged = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    this.setState({ diversionReasonNotes: e.target.value });
  };

  private handleMapChange = (evt: MapChangeEvent): void => {
    // CardMap fires onChange with partial payloads (e.g. Circle load reports the
    // circle only, no location; marker drag reports location only). Merge each
    // partial update into mapValue instead of overwriting, so a partial event
    // doesn't wipe fields set by an earlier plant or address selection.
    if (evt.circle) {
      this.setState(prev => ({
        geoZone: circleToTicketGeozone(evt.circle),
        mapValue: {
          ...prev.mapValue,
          circle: evt.circle,
          ...(evt.location ? { location: evt.location } : {}),
        },
      }));
    } else if (evt.polygon) {
      // v2 tickets are circle-only; approximate a drawn polygon as a bounding circle
      // rather than silently dropping it (see DEV-2580 — polygon ticket support deferred).
      this.setState(prev => ({
        geoZone: polygonPointsToTicketGeozone(evt.polygon?.points),
        mapValue: {
          ...prev.mapValue,
          polygon: evt.polygon,
          ...(evt.location ? { location: evt.location } : {}),
        },
      }));
    } else if (evt.location) {
      this.setState(prev => ({ mapValue: { ...prev.mapValue, location: evt.location } }));
    }
  };

  private confirmApply = (): void => {
    this.props.openModal({
      modalType: ACCENT,
      modalTitle: ARE_YOU_SURE,
      modalBody: (
        <span>
          <p>{DELIVERY_LOCATION_CONFIRM}</p>
        </span>
      ),
      modalOpen: true,
      acceptDialog: this.applyChange,
      acceptText: APPLY_LABEL,
      cancelText: CANCEL_LABEL,
      disabled: false,
      noActions: false,
    });
  };

  private applyChange = async (): Promise<void> => {
    const { selectedTicket } = this.props;
    const {
      geoZone,
      mode,
      confirmedAddress,
      selectedPlant,
      diversionReason,
      diversionReasonNotes,
    } = this.state;
    if (!geoZone) {
      return;
    }

    // Both optional: the reason goes as the enum member name, and the notes ride along
    // only for Other. Undefined keeps them off the request entirely.
    const reason = diversionReason || undefined;
    const notes =
      diversionReason === DIVERSION_REASON_OTHER && diversionReasonNotes.trim()
        ? diversionReasonNotes.trim()
        : undefined;

    // Build the address string the ticket panel will display. In plant mode we
    // reconstruct it from the plant's address parts (matches the existing
    // `line1\ncity, state zip` shape TicketDetails splits on). In address mode
    // we forward the canonical address the dispatcher confirmed. Without this, the
    // PATCH only moves the geozone and the dispatcher still sees the pre-divert
    // address in the panel.
    const nextAddress =
      mode === 'plant' && selectedPlant
        ? formatPlantAddress(selectedPlant)
        : mode === 'address' && confirmedAddress.trim()
          ? confirmedAddress.trim()
          : undefined;

    const updatedTicket: TicketDto = {
      ...selectedTicket,
      geoZone,
      ...(nextAddress ? { address: nextAddress } : {}),
    };

    this.setState({ submitting: true });
    try {
      // updateDeliveryLocation returns the persisted TicketDto — push it into
      // selectedTicket (drives the drilldown panel) AND the tickets list (drives
      // the map marker/geozone). The websocket SYNC_TICKET path only updates
      // the list, so relying on it leaves the drilldown showing the pre-divert
      // address until the user re-selects the ticket.
      const persisted = await updateDeliveryLocation(updatedTicket, reason, notes);
      if (persisted) {
        this.props.selectTicket(persisted);
        this.props.syncTicket(persisted);
      }
      this.props.openSnackbar({
        snackbarBody: DELIVERY_LOCATION_UPDATE_SUCCESS,
        dataTest: 'update-delivery-location-success-snackbar',
        snackbarType: SUCCESS as any,
      });
      this.props.needToRefreshData(true);
      // Dismiss the confirm dialog, then close (unmount) this modal last so no
      // setState runs on an unmounted component.
      this.props.closeModal();
      this.props.onClose();
    } catch (e: any) {
      // Surface the failure via snackbar (a different Redux slice than the modal),
      // then dismiss the confirm dialog. Using openFailModal here would be clobbered
      // by closeModal on the same SET_MODAL_OPTIONS slice. Keep this modal open so
      // the dispatcher can retry.
      this.setState({ submitting: false });
      this.props.openSnackbar({
        snackbarBody: `${DELIVERY_LOCATION_UPDATE_ERROR} ${e?.message ?? ''}`.trim(),
        dataTest: 'update-delivery-location-error-snackbar',
        snackbarType: ERROR as any,
      });
      this.props.closeModal();
    }
  };

  private renderModeToggle(): JSX.Element {
    const { mode } = this.state;
    return (
      <div className={styles.modeToggle} data-test="delivery-location-mode-toggle">
        <Button
          name={DELIVERY_LOCATION_SELECT_PLANT}
          dataTest="delivery-location-mode-plant"
          buttonClassName={cx('tt-btn-secondary', { 'tt-btn-secondary--accent': mode === 'plant' })}
          onClick={() => this.setMode('plant')}
        />
        <Button
          name={DELIVERY_LOCATION_ENTER_ADDRESS}
          dataTest="delivery-location-mode-address"
          buttonClassName={cx('tt-btn-secondary', {
            'tt-btn-secondary--accent': mode === 'address',
          })}
          onClick={() => this.setMode('address')}
        />
      </div>
    );
  }

  private renderPlantMode(): JSX.Element {
    return (
      <div ref={this.plantModeRef}>
        <DivertPlantPicker
          ticketProductLine={this.props.ticketProductLine}
          onSelected={this.handlePlantSelected}
          onCleared={() => this.setState({ geoZone: null, mapValue: {} })}
          onError={this.handlePlantListError}
        />
      </div>
    );
  }

  private renderMap(): JSX.Element {
    const { mapValue, mode } = this.state;
    const location = mapValue.location;
    // No location yet — show a placeholder CTA in the map area instead of
    // collapsing it, so users know something's supposed to appear there.
    if (!location || !isLocationValid(location)) {
      return (
        <div
          className={cx(styles.mapField, styles.mapPlaceholder)}
          data-test="delivery-location-map-placeholder"
        >
          <span className={styles.mapPlaceholderIcon} aria-hidden="true">
            <i className="icon-map" />
          </span>
          <p className={styles.mapPlaceholderText}>Select a plant or address to preview</p>
        </div>
      );
    }
    // Remount the map when the target location changes so it recenters. CardMap
    // locks onto mapRef.getCenter() after load, so prop updates alone don't
    // move the viewport.
    const key = `${location.latitude ?? location.lat},${location.longitude ?? location.lng}`;
    // Plant mode: read-only, geozone is already fixed by the plant's zone.
    // Address mode: editable so the auto-seeded geozone circle can still be
    // resized/moved — but the pin/circle/polygon draw tools stay hidden
    // (showDrawingManager=false), since the address is confirmed from the picker.
    const isAddressMode = mode === 'address';
    return (
      <div className={styles.mapField} data-test="delivery-location-map">
        <CardMap
          key={key}
          value={mapValue}
          onChange={this.handleMapChange}
          isEditable={isAddressMode}
          showSearchBox={false}
          showDrawingManager={false}
          showMarker
          mapContainerStyle={{ height: '100%', width: '100%', maxWidth: '100%' }}
          containerStyle={{ height: '100%', width: '100%', maxWidth: '100%' }}
        />
      </div>
    );
  }

  private renderReasonPicker(): JSX.Element {
    const { diversionReason, diversionReasonNotes } = this.state;
    // "None" first, mirroring CardSelect's optional-select convention; its empty value
    // clears the selection.
    const items = [
      { label: 'None', value: '', isNone: true },
      ...DIVERSION_REASON_OPTIONS.map(option => ({ label: option.label, value: option.value })),
    ];
    // DropDownList displays initialSelected, not its own selection state, so the chosen
    // option's label is fed back here; "None" doubles as the unselected display.
    const selectedLabel =
      DIVERSION_REASON_OPTIONS.find(option => option.value === diversionReason)?.label ?? 'None';
    return (
      <div className={styles.reasonField} data-test="diversion-reason-field">
        <Label>{DIVERSION_REASON_LABEL}</Label>
        <DropDownList
          items={items}
          initialSelected={{ label: selectedLabel }}
          updateSelected={this.handleReasonSelected}
          dataTest="diversion-reason-dropdown"
        />
        {diversionReason === DIVERSION_REASON_OTHER ? (
          <textarea
            className={styles.reasonNotes}
            data-test="diversion-reason-notes"
            placeholder={DIVERSION_REASON_NOTES_PLACEHOLDER}
            maxLength={DIVERSION_REASON_NOTES_MAX_LENGTH}
            value={diversionReasonNotes}
            onChange={this.handleNotesChanged}
          />
        ) : null}
      </div>
    );
  }

  render(): JSX.Element {
    const { isOpen, onClose, selectedTicket } = this.props;
    const { mode, geoZone, mapValue, submitting } = this.state;
    return (
      <AddFeatureModal
        title={UPDATE_DELIVERY_LOCATION_TITLE}
        isOpen={isOpen}
        onCancel={onClose}
        modalSize="md"
      >
        <div data-test="update-delivery-location-modal">
          <div className={styles.body}>
            {this.renderModeToggle()}
            {mode === 'plant' ? (
              <>
                {this.renderPlantMode()}
                {this.renderMap()}
              </>
            ) : (
              <>
                <DeliveryAddressPicker
                  bias={selectedTicket?.geoZone?.zone?.circle?.center}
                  onConfirmed={this.handleAddressConfirmed}
                  onCleared={this.handleAddressCleared}
                  onError={this.handleLookupError}
                />
                {/* Map appears only once an address is confirmed; before that the
                    picker's list fills the slot. */}
                {mapValue.location ? this.renderMap() : null}
              </>
            )}
            {this.renderReasonPicker()}
          </div>
          <div className={styles.actions}>
            <Button
              name={CANCEL_LABEL}
              dataTest="delivery-location-cancel"
              buttonClassName="tt-btn-secondary"
              onClick={onClose}
            />
            <Button
              name={APPLY_LABEL}
              dataTest="delivery-location-apply"
              buttonClassName="tt-btn-primary"
              disabled={!geoZone || submitting}
              onClick={this.confirmApply}
            />
          </div>
        </div>
      </AddFeatureModal>
    );
  }
}

const mapDispatchToProps = {
  openModal,
  closeModal,
  openSnackbar,
  needToRefreshData,
  selectTicket,
  syncTicket,
};

// No mapStateToProps: the destination list is tenant-wide, so this modal reads nothing
// from the store — in particular not state.plantList, whose loaders drive the map.
export default connect(null, mapDispatchToProps)(UpdateDeliveryLocationModal);
